'use client';

import { Link } from '@heroui/link';

export default function Page() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              Terms and Conditions
            </h1>
            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              Effective Date: March 2025
            </p>
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container space-y-12 container-md">
          <p>
            Welcome to PayStop. These Terms and Conditions (“Terms”) govern your use of our website,
            mobile applications, and services (the “Platform”). By accessing or using our services,
            you agree to be bound by these Terms. If you do not agree to these Terms, you must
            immediately cease using the Platform.
          </p>
          <p>
            PayStop provides a platform for users to deposit, withdraw, and transfer funds through a
            network of agents, leveraging API integrations with digital banking services, mobile
            wallets, and traditional bank accounts. Please read these Terms carefully to understand
            your rights and responsibilities.
          </p>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">1. General Terms of Use</h2>
            <div className="pl-4">
              <p className="mb-2">
                <strong>Eligibility:</strong> You must be at least 18 years old to use the Platform.
                By using the Platform, you confirm that you are of legal age and capable of entering
                into a binding contract.
              </p>
              <p className="mb-2">
                <strong>Account Registration:</strong>
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Customers:</strong> You do not need to sign up for a PayStop account. You
                  can access your digital banking account, mobile wallet, or traditional bank
                  account through our agent network.
                </li>
                <li>
                  <strong>Agents:</strong> To become an agent, you must register with PayStop and
                  fund your account via a commercial bank. You may be required to provide valid
                  identification and/or business documentation.
                </li>
              </ul>
              <p className="mb-2">
                <strong>Acceptable Use:</strong> You agree to use the Platform only for lawful
                purposes, and you will not engage in activities that violate any applicable laws or
                regulations, including but not limited to fraud, money laundering, and identity
                theft.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">2. Services Provided</h2>
            <div className="pl-4">
              <p className="mb-2">PayStop provides the following services:</p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Deposit and Withdrawal Services:</strong> Customers can deposit or
                  withdraw funds through agents connected to their account providers via API
                  integration.
                </li>
                <li>
                  <strong>Transaction Processing:</strong> We facilitate digital banking
                  transactions, mobile wallet transfers, and traditional bank account transfers.
                  Transactions are subject to applicable fees and limits.
                </li>
                <li>
                  <strong>Agent Network:</strong> PayStop operates through a network of Super Agents
                  and Agent Admins who manage transactions and offer assistance to customers. Agents
                  are responsible for facilitating deposits and withdrawals in exchange for a fee.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">3. Fees and Charges</h2>
            <div className="pl-4">
              <p className="mb-4">
                PayStop charges fees for transactions facilitated through the Platform. These fees
                may vary based on the type of service (deposit, withdrawal, transfer) and the
                service provider (digital bank, mobile wallet, or traditional bank). The applicable
                fees will be clearly displayed during the transaction process.
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Agent Rewards:</strong> Agents will receive a reward based on the volume
                  of transactions they facilitate. The reward may be a percentage of the transaction
                  amount, as specified by PayStop.
                </li>
                <li>
                  <strong>Payment Method Fees:</strong> Any transaction fees applied by your bank,
                  digital bank, or mobile wallet provider are your responsibility and are not
                  covered by PayStop.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">4. Account Security</h2>
            <div className="pl-4">
              <p className="mb-4">
                You are responsible for maintaining the confidentiality of your account details,
                including your password and any PINs. You must immediately notify PayStop if you
                suspect any unauthorized access to your account or if your account credentials have
                been compromised.
              </p>
              <p className="mb-2">
                PayStop implements various security measures to protect your data and transactions,
                but you are also responsible for ensuring your own devices and accounts are secure.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">5. Privacy and Data Protection</h2>
            <div className="pl-4">
              <p className="mb-2">
                Your privacy is important to us. We collect and process your personal data in
                accordance with our Privacy Notice, which outlines how we handle your information.
                By using the Platform, you consent to the collection and processing of your personal
                data as described in the <Link href="/privacy-notice">Privacy Notice.</Link>
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">6. Prohibited Activities</h2>
            <div className="pl-4">
              <p className="mb-2">You agree not to engage in the following activities:</p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Fraudulent Activities:</strong> Using the Platform for fraudulent
                  activities, including money laundering or identity theft.
                </li>
                <li>
                  <strong>Misuse of the Platform:</strong> Attempting to reverse engineer, disrupt,
                  or gain unauthorized access to the Platform, servers, or networks associated with
                  PayStop.
                </li>
                <li>
                  <strong>Inappropriate Content:</strong>Uploading or transmitting any content that
                  is harmful, offensive, or illegal.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">7. Termination of Access</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop reserves the right to suspend or terminate your access to the Platform at
                any time if:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>You violate these Terms or any applicable laws.</li>
                <li>You engage in fraudulent activities or breach security protocols.</li>
                <li>We are required to do so to comply with legal or regulatory requirements.</li>
              </ul>
              <p className="mb-2">
                Upon termination, you will no longer have access to the Platform or any associated
                services.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">8. Account Closure</h2>
            <div className="pl-4">
              <p className="mb-4">
                If you wish to close your PayStop account, you must submit a formal request to our
                support team at support@paystop.io. Before account closure, any outstanding
                balances, pending transactions, or obligations must be settled. PayStop reserves the
                right to deny account closure if legal, financial, or regulatory issues are
                associated with the account. Once closed, your account cannot be reactivated, and
                all stored data will be deleted per our data retention policy.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">9. Limitation of Liability</h2>
            <div className="pl-4">
              <p className="mb-4">
                PayStop will not be liable for any indirect, incidental, or consequential damages
                arising from the use of the Platform, including, but not limited to, transaction
                errors, system failures, or the actions of third-party service providers.
              </p>
              <p className="mb-2">
                We do not guarantee the continuous availability of the Platform, and we are not
                liable for any interruptions or downtime.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">10. Dispute Resolution</h2>
            <div className="pl-4">
              <p className="mb-2">
                Any disputes arising from the use of the Platform shall be resolved through binding
                arbitration. The arbitration shall be conducted in Cameroon, and the decision of the
                arbitrator will be final and binding.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">11. Governing Law</h2>
            <div className="pl-4">
              <p className="mb-2">
                These Terms are governed by the laws of Cameroon, and any legal proceedings related
                to these Terms shall be subject to the exclusive jurisdiction of the courts in
                Cameroon.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">11. Amendments to Terms</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop reserves the right to modify or amend these Terms at any time. Any changes
                to these Terms will be posted on this page, and the updated version will take effect
                immediately upon publication. It is your responsibility to review these Terms
                regularly.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">12. Contact Us</h2>
            <div className="pl-4">
              <p className="mb-2">
                If you have any questions or concerns about these Terms and Conditions, please
                contact us at:
              </p>
              <p className="mb-2">Email: support@paystop.io</p>
              <p className="mb-2">Phone: +237 676962723</p>
              <p className="mb-8">
                Address: Crystal Finance Commercial Avenue Bamenda, P.O. Box 168 Bamenda Cameroon
              </p>
              <i className="mb-2">
                By using the PayStop Platform, you acknowledge that you have read, understood, and
                agree to be bound by these Terms and Conditions.
              </i>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
